import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import { apiService } from '../services/apiService';
import { renderMathContent, renderAnswerChoice } from './amcMathRendering';
import 'katex/dist/katex.min.css';

interface Problem {
  id: string;
  problemNumber: number;
  problemStatement: string;
  answerChoices: {
    A?: string;
    B?: string;
    C?: string;
    D?: string;
    E?: string;
  };
  difficulty: string;
  mainTopic: string;
  subtopic?: string;
  aopsSourceLink?: string;
  problemImage?: string;
}

interface Contest {
  id: string;
  name: string;
  year: number;
  contestType: string;
  version: string;
  problems: Problem[];
}

interface TestSession {
  contest: Contest;
  currentQuestionNumber: number;
  totalQuestions: number;
  attemptedProblems: Record<number, {
    answerGiven: string;
    isCorrect: boolean;
    timeSpent: number;
    attemptDate: string;
  }>;
  isCompleted: boolean;
}

interface AMCTestInterfaceProps {
  contestId: string;
  onComplete: () => void;
  onBack: () => void;
}

const AMCTestInterface: React.FC<AMCTestInterfaceProps> = ({ contestId, onComplete, onBack }) => {
  const { token } = useUser();
  const [testSession, setTestSession] = useState<TestSession | null>(null);
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [selectedAnswer, setSelectedAnswer] = useState<string>('');
  const [questionStartTime, setQuestionStartTime] = useState<number>(Date.now());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showTimerSettings, setShowTimerSettings] = useState(true);
  const [timerEnabled, setTimerEnabled] = useState(true);
  const [testStartTime, setTestStartTime] = useState<number | null>(null);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [testTimeUp, setTestTimeUp] = useState(false);
  
  const TEST_DURATION = 75 * 60 * 1000; // 75 minutes in milliseconds

  useEffect(() => {
    if (token) {
      startTestSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contestId, token]);

  useEffect(() => {
    setQuestionStartTime(Date.now());
    setSelectedAnswer('');
    
    if (testSession?.attemptedProblems[currentQuestion]) {
      setSelectedAnswer(testSession.attemptedProblems[currentQuestion].answerGiven);
    }
  }, [currentQuestion, testSession]);

  // Timer effect
  useEffect(() => {
    if (!testStartTime || !timerEnabled || testTimeUp) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const elapsed = now - testStartTime;
      setTimeElapsed(elapsed);

      if (elapsed >= TEST_DURATION) {
        setTestTimeUp(true);
        clearInterval(interval);
        // Auto-submit test when time is up
        setTimeout(() => {
          onComplete();
        }, 2000);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [testStartTime, timerEnabled, testTimeUp, TEST_DURATION, onComplete]);

  const startTestSession = async () => {
    if (!token) {
      setError('Authentication required');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await apiService.post('/amc/test/start', { contestId });
      setTestSession(data.testSession);
      setCurrentQuestion(data.testSession.currentQuestionNumber);
      
      // If test is already completed, redirect to results
      if (data.testSession.isCompleted) {
        onComplete();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start test');
    } finally {
      setLoading(false);
    }
  };

  const startTest = () => {
    setShowTimerSettings(false);
    setTestStartTime(Date.now());
  };

  const formatTime = (milliseconds: number): string => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const getTimeRemaining = (): string => {
    if (!testStartTime || testTimeUp) return '00:00';
    const remaining = Math.max(0, TEST_DURATION - timeElapsed);
    return formatTime(remaining);
  };

  const getTimeColor = (): string => {
    if (!testStartTime) return '#64748b';
    const remaining = TEST_DURATION - timeElapsed;
    const percentRemaining = remaining / TEST_DURATION;
    
    if (percentRemaining <= 0.1) return '#ef4444'; // Red - last 10%
    if (percentRemaining <= 0.25) return '#f59e0b'; // Orange - last 25%
    return '#10b981'; // Green - plenty of time
  };

  const submitAnswer = async () => {
    if (!testSession || !selectedAnswer || !token || testTimeUp) return;

    const currentProblem = testSession.contest.problems.find(p => p.problemNumber === currentQuestion);
    if (!currentProblem) return;

    try {
      setSubmitting(true);
      const timeSpent = Math.round((Date.now() - questionStartTime) / 1000);

      const result = await apiService.post('/amc/test/answer', {
        problemId: currentProblem.id,
        answerGiven: selectedAnswer,
        timeSpent
      });
      
      setTestSession(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          attemptedProblems: {
            ...prev.attemptedProblems,
            [currentQuestion]: {
              answerGiven: selectedAnswer,
              isCorrect: result.result.isCorrect,
              timeSpent,
              attemptDate: new Date().toISOString()
            }
          }
        };
      });

      // Automatically move to next question after submission
      if (currentQuestion < testSession.totalQuestions) {
        setCurrentQuestion(prev => prev + 1);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit answer');
    } finally {
      setSubmitting(false);
    }
  };

  const navigateToQuestion = (questionNumber: number) => {
    if (testTimeUp) return;
    setCurrentQuestion(questionNumber);
  };

  const getCurrentProblem = (): Problem | null => {
    if (!testSession) return null;
    return testSession.contest.problems.find(p => p.problemNumber === currentQuestion) || null;
  };

  const isAnswered = (questionNumber: number) => {
    return testSession?.attemptedProblems[questionNumber] !== undefined;
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #3F8EF6 0%, #A0F0D0 100%)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '3px solid rgba(255, 255, 255, 0.3)',
            borderTopColor: 'white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <p style={{ color: 'white', fontSize: '18px' }}>Loading test...</p>
        </div>
        <style>
          {`@keyframes spin { to { transform: rotate(360deg); } }`}
        </style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #3F8EF6 0%, #A0F0D0 100%)'
      }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          padding: '40px',
          borderRadius: '16px',
          textAlign: 'center',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }}>
          <p style={{ color: '#ef4444', marginBottom: '20px', fontSize: '18px' }}>{error}</p>
          <button onClick={startTestSession} style={{
            background: 'linear-gradient(135deg, #3F8EF6, #A0F0D0)',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '8px',
            fontWeight: '600',
            cursor: 'pointer',
            fontSize: '16px'
          }}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!testSession) {
    return <div>Test session not found</div>;
  }

  // Timer Settings Modal
  if (showTimerSettings) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #3F8EF6 0%, #A0F0D0 100%)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          padding: '40px',
          maxWidth: '500px',
          width: '90%',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          textAlign: 'center'
        }}>
          <h2 style={{
            fontSize: '28px',
            fontWeight: '700',
            background: 'linear-gradient(135deg, #3F8EF6, #A0F0D0)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '8px'
          }}>Ready to Start?</h2>
          <p style={{ color: '#64748b', fontSize: '16px', marginBottom: '32px' }}>
            {testSession.contest.name}
          </p>

          <div style={{
            background: '#f8fafc',
            padding: '24px',
            borderRadius: '12px',
            marginBottom: '32px',
            textAlign: 'left'
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
              Test Information
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', color: '#64748b' }}>
              <div>• 25 multiple choice questions</div>
              <div>• 75 minutes total time</div>
              <div>• 6 points for correct, 1.5 for blank, 0 for wrong</div>
              <div>• You can navigate between questions freely</div>
            </div>
          </div>

          <div style={{
            background: '#f8fafc',
            padding: '20px',
            borderRadius: '12px',
            marginBottom: '32px',
            border: '2px solid #e2e8f0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <input
                type="checkbox"
                id="timerToggle"
                checked={timerEnabled}
                onChange={(e) => setTimerEnabled(e.target.checked)}
                style={{
                  width: '18px',
                  height: '18px',
                  accentColor: '#3F8EF6'
                }}
              />
              <label htmlFor="timerToggle" style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#1e293b',
                cursor: 'pointer'
              }}>
                Show timer during test
              </label>
            </div>
            <p style={{ fontSize: '14px', color: '#64748b', marginLeft: '30px' }}>
              {timerEnabled 
                ? 'Timer will be visible and count down from 75:00 to 00:00' 
                : 'Timer will be hidden, but test will still end after 75 minutes'}
            </p>
          </div>

          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
            <button onClick={onBack} style={{
              background: '#f8fafc',
              border: '2px solid #e2e8f0',
              borderRadius: '8px',
              padding: '12px 24px',
              fontWeight: '600',
              color: '#64748b',
              cursor: 'pointer',
              fontSize: '16px',
              transition: 'all 0.2s ease'
            }}>
              Back to Tests
            </button>
            <button onClick={startTest} style={{
              background: 'linear-gradient(135deg, #3F8EF6, #A0F0D0)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 32px',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '16px',
              transition: 'all 0.2s ease'
            }}>
              Start Test
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Time's Up Modal
  if (testTimeUp) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #3F8EF6 0%, #A0F0D0 100%)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          padding: '40px',
          maxWidth: '500px',
          width: '90%',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '64px',
            marginBottom: '16px'
          }}>⏰</div>
          <h2 style={{
            fontSize: '28px',
            fontWeight: '700',
            color: '#ef4444',
            marginBottom: '8px'
          }}>Time's Up!</h2>
          <p style={{ color: '#64748b', fontSize: '16px', marginBottom: '32px' }}>
            Your 75 minutes have ended. Submitting your test now...
          </p>
          <div style={{
            width: '100%',
            height: '4px',
            background: '#e2e8f0',
            borderRadius: '2px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: '100%',
              height: '100%',
              background: 'linear-gradient(135deg, #3F8EF6, #A0F0D0)',
              animation: 'pulse 1s ease-in-out infinite'
            }}></div>
          </div>
          <style>
            {`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }`}
          </style>
        </div>
      </div>
    );
  }

  const currentProblem = getCurrentProblem();
  if (!currentProblem) {
    return <div>Problem not found</div>;
  }

  const answeredCount = Object.keys(testSession.attemptedProblems).length;

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #3F8EF6 0%, #A0F0D0 100%)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif'
    }}>
      <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '20px' }}>
        {/* Header */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          padding: '20px 30px',
          marginBottom: '24px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <button onClick={onBack} style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            padding: '8px 16px',
            textDecoration: 'none',
            color: '#64748b',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}>
            <ChevronLeft size={16} />
            Back to Tests
          </button>
          
          <div style={{ textAlign: 'center' }}>
            <h1 style={{
              fontSize: '28px',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #3F8EF6, #A0F0D0)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              marginBottom: '4px'
            }}>{testSession.contest.name}</h1>
            <div style={{ color: '#64748b', fontSize: '14px' }}>
              Progress: {answeredCount}/{testSession.totalQuestions} answered
            </div>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {timerEnabled && testStartTime && (
              <div style={{
                background: 'rgba(255, 255, 255, 0.9)',
                color: getTimeColor(),
                padding: '8px 16px',
                borderRadius: '20px',
                fontWeight: '700',
                fontSize: '16px',
                border: `2px solid ${getTimeColor()}`,
                fontFamily: 'monospace',
                minWidth: '80px',
                textAlign: 'center'
              }}>
                {getTimeRemaining()}
              </div>
            )}
            <div style={{
              background: 'linear-gradient(135deg, #3F8EF6, #A0F0D0)',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '20px',
              fontWeight: '600',
              fontSize: '14px'
            }}>
              Question {currentQuestion} of {testSession.totalQuestions}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          padding: '30px',
          marginBottom: '24px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '24px'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #3F8EF6, #A0F0D0)',
              color: 'white',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: '700',
              fontSize: '18px'
            }}>
              {currentProblem.problemNumber}
            </div>
            <h2 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#1e293b'
            }}>Problem {currentProblem.problemNumber}</h2>
          </div>

          <div style={{
            marginBottom: '32px',
            background: '#f8fafc',
            borderRadius: '12px',
            borderLeft: '4px solid #3F8EF6',
            padding: '20px'
          }}>
            <div style={{
              fontSize: '16px',
              lineHeight: '1.6',
              color: '#334155'
            }}>
              {renderMathContent(currentProblem.problemStatement)}
            </div>
            
            {currentProblem.problemImage && (
              <div style={{
                textAlign: 'center',
                marginTop: '20px'
              }}>
                <img 
                  src={currentProblem.problemImage} 
                  alt={`Problem ${currentProblem.problemNumber} diagram`}
                  style={{
                    maxWidth: '100%',
                    height: 'auto'
                  }}
                  onError={(e) => {
                    console.error('Failed to load problem image:', e);
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>

          <div>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#1e293b',
              marginBottom: '20px'
            }}>Select your answer:</h3>
            
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '16px',
              justifyContent: 'space-between'
            }}>
              {Object.entries(currentProblem.answerChoices).map(([choice, value]) => (
                value && (
                  <div
                    key={choice}
                    onClick={() => !testTimeUp && setSelectedAnswer(choice)}
                    style={{
                      background: selectedAnswer === choice ? 'linear-gradient(135deg, #3F8EF610, #A0F0D010)' : '#ffffff',
                      border: `2px solid ${selectedAnswer === choice ? '#3F8EF6' : '#e2e8f0'}`,
                      borderRadius: '12px',
                      padding: '12px 16px',
                      cursor: testTimeUp ? 'not-allowed' : 'pointer',
                      opacity: testTimeUp ? 0.6 : 1,
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      flex: '1',
                      minWidth: '120px',
                      justifyContent: 'center'
                    }}
                    onMouseEnter={(e) => {
                      if (selectedAnswer !== choice && !testTimeUp) {
                        e.currentTarget.style.borderColor = '#3F8EF6';
                        e.currentTarget.style.background = '#f8fafc';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(63, 142, 246, 0.15)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedAnswer !== choice && !testTimeUp) {
                        e.currentTarget.style.borderColor = '#e2e8f0';
                        e.currentTarget.style.background = '#ffffff';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }
                    }}
                  >
                    <div style={{
                      width: '20px',
                      height: '20px',
                      border: `2px solid ${selectedAnswer === choice ? '#3F8EF6' : '#cbd5e1'}`,
                      borderRadius: '50%',
                      position: 'relative',
                      transition: 'all 0.2s ease',
                      background: selectedAnswer === choice ? '#3F8EF6' : 'transparent'
                    }}>
                      {selectedAnswer === choice && (
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          width: '8px',
                          height: '8px',
                          background: 'white',
                          borderRadius: '50%'
                        }}></div>
                      )}
                    </div>
                    <span style={{
                      fontWeight: '600',
                      color: '#475569',
                      minWidth: '20px'
                    }}>{choice}.</span>
                    <span style={{
                      color: '#334155',
                      fontWeight: '500'
                    }}>
                      {renderAnswerChoice(value)}
                    </span>
                  </div>
                )
              ))}
            </div>
          </div>

          {/* Navigation */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '32px',
            paddingTop: '24px',
            borderTop: '1px solid #e2e8f0',
            gap: '16px'
          }}>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => navigateToQuestion(Math.max(1, currentQuestion - 1))}
                disabled={currentQuestion === 1 || testTimeUp}
                style={{
                  background: '#ffffff',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  fontWeight: '500',
                  color: '#64748b',
                  cursor: currentQuestion === 1 || testTimeUp ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: currentQuestion === 1 || testTimeUp ? 0.5 : 1
                }}
                onMouseEnter={(e) => {
                  if (currentQuestion !== 1 && !testTimeUp) {
                    e.currentTarget.style.borderColor = '#3F8EF6';
                    e.currentTarget.style.color = '#3F8EF6';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.color = '#64748b';
                }}
              >
                <ChevronLeft size={16} />
                Previous
              </button>
            </div>
            
            <div style={{ display: 'flex', gap: '12px' }}>
              {selectedAnswer && !testTimeUp && (
                <button
                  onClick={submitAnswer}
                  disabled={submitting || testTimeUp}
                  style={{
                    background: 'linear-gradient(135deg, #3F8EF6, #A0F0D0)',
                    color: 'white',
                    border: '2px solid #3F8EF6',
                    borderRadius: '8px',
                    padding: '10px 20px',
                    fontWeight: '500',
                    cursor: submitting || testTimeUp ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    opacity: submitting || testTimeUp ? 0.7 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!submitting && !testTimeUp) {
                      e.currentTarget.style.background = 'linear-gradient(135deg, #2E7DE5, #8FE9BF)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #3F8EF6, #A0F0D0)';
                  }}
                >
                  {submitting ? 'Submitting...' : (isAnswered(currentQuestion) ? 'Update Answer' : 'Submit Answer')}
                </button>
              )}
              
              {/* Show "See Results" button if all questions are answered OR test timed out */}
              {(answeredCount === testSession.totalQuestions || testTimeUp) ? (
                <button
                  onClick={onComplete}
                  style={{
                    background: 'linear-gradient(135deg, #10b981, #34d399)',
                    color: 'white',
                    border: '2px solid #10b981',
                    borderRadius: '8px',
                    padding: '10px 20px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #059669, #10b981)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #10b981, #34d399)';
                  }}
                >
                  {testTimeUp ? 'View Results (Timed Out)' : 'See Results'}
                  <ChevronRight size={16} />
                </button>
              ) : (
                <button
                  onClick={() => navigateToQuestion(Math.min(testSession.totalQuestions, currentQuestion + 1))}
                  disabled={currentQuestion === testSession.totalQuestions || testTimeUp}
                  style={{
                    background: '#ffffff',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '10px 20px',
                    fontWeight: '500',
                    color: '#64748b',
                    cursor: currentQuestion === testSession.totalQuestions || testTimeUp ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    opacity: currentQuestion === testSession.totalQuestions || testTimeUp ? 0.5 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (currentQuestion !== testSession.totalQuestions && !testTimeUp) {
                      e.currentTarget.style.borderColor = '#3F8EF6';
                      e.currentTarget.style.color = '#3F8EF6';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e2e8f0';
                    e.currentTarget.style.color = '#64748b';
                  }}
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Questions Grid */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#1e293b',
            marginBottom: '20px'
          }}>Questions</h3>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(60px, 1fr))',
            gap: '12px'
          }}>
            {Array.from({ length: testSession.totalQuestions }, (_, i) => i + 1).map((num) => (
              <div
                key={num}
                onClick={() => !testTimeUp && navigateToQuestion(num)}
                style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '600',
                  cursor: testTimeUp ? 'not-allowed' : 'pointer',
                  opacity: testTimeUp ? 0.6 : 1,
                  transition: 'all 0.2s ease',
                  border: '2px solid #e2e8f0',
                  background: num === currentQuestion 
                    ? 'linear-gradient(135deg, #3F8EF6, #A0F0D0)'
                    : isAnswered(num) 
                      ? '#10b981' 
                      : '#ffffff',
                  color: num === currentQuestion || isAnswered(num) ? 'white' : '#64748b',
                  borderColor: num === currentQuestion 
                    ? '#3F8EF6' 
                    : isAnswered(num) 
                      ? '#10b981' 
                      : '#e2e8f0',
                  boxShadow: num === currentQuestion ? '0 4px 12px rgba(63, 142, 246, 0.3)' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (!testTimeUp) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!testTimeUp) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = num === currentQuestion ? '0 4px 12px rgba(63, 142, 246, 0.3)' : 'none';
                  }
                }}
              >
                {num}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AMCTestInterface;